import React, { Suspense, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { IgButton, IgLink, IgTypography } from "@intelligems/ig-theme";
import {
  GetScriptStatusResponseBodyType,
  OrganizationDto,
} from "@intelligems/ig-types";
import { AlertColor } from "@mui/material/Alert/Alert";
import {
  allIntegrationGuides,
  igDirectScriptAdd,
  scriptUpdateDocLink,
} from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";
import { useQueryClient } from "@tanstack/react-query";

import { useGetExperiencesList } from "../../../api/experiences-list/get-experiences-list";
import {
  useGetOrg,
  useGetOrgId,
} from "../../../api/organizations/get-organization";
import { organizationScriptStatusKey } from "../../../api/queryKeys";
import { useGetScriptStatus } from "../../../api/shops/get-script-status";
import { SETTINGS_ROUTES } from "../../../routes/routes";
import { setDialog } from "../../../store/slices/global";
import { IgAlert } from "../../Utils/IgAlert";

const HelpDocLink = () => (
  <IgTypography>
    Please enable Intelligems in your theme.
    {"  "}
    <IgLink
      onClick={() => {
        openNewTab(allIntegrationGuides, "Integration Guides");
      }}
    >
      Learn more
    </IgLink>
    .
  </IgTypography>
);

interface ScriptStatusAlertProps {
  hideDismiss?: boolean;
  dismissId?: string;
}

interface ChildScriptStatusAlertProps extends ScriptStatusAlertProps {
  org: OrganizationDto;
  scriptStatus: GetScriptStatusResponseBodyType;
}

const NormalScriptStatusAlert = ({
  scriptStatus,
  org,
  hideDismiss,
  dismissId,
}: ChildScriptStatusAlertProps) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const scriptStatusQuery = useGetScriptStatus(org.id);
  const dispatch = useDispatch();
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  const content = useMemo(() => {
    if (experiencesList.isSuccess) {
      if (scriptStatus.type === "error") {
        return null;
      }

      if (scriptStatus.type === "missing") {
        return {
          title: "Intelligems Not Detected in your Theme",
          message: <HelpDocLink />,
          severity: "error",
        };
      }

      // if (scriptStatus.type === "importMapError") {
      //   return {
      //     title: "URGENT: Move Intelligems Script Location",
      //     message: (
      //       <IgTypography>
      //         Please follow{" "}
      //         <IgLink
      //           onClick={() => {
      //             openNewTab(importMapDocLink, "Integration Guides");
      //           }}
      //         >
      //           these steps
      //         </IgLink>{" "}
      //         to easily fix your script.
      //       </IgTypography>
      //     ),
      //     severity: "error",
      //   };
      // }

      if (
        scriptStatus.type === "isCjs" &&
        location.pathname !== "/events-manager"
      ) {
        return {
          title: "Script Outdated",
          severity: "warning",
          message: (
            <IgTypography>
              Your Intelligems script needs an update to stay compatible with
              our latest features. To update your script and ensure access to
              our latest improvements,{" "}
              <IgLink
                onClick={() => {
                  openNewTab(scriptUpdateDocLink, "Integration Guides");
                }}
              >
                please follow these steps.
              </IgLink>
            </IgTypography>
          ),
        };
      }

      if (scriptStatus.type === "multipleScriptsWithAppEmbedFound") {
        return {
          title: "Multiple Intelligems Scripts Detected",
          message: (
            <IgTypography>
              An App Embed and a Script Tag are detected. Please remove the App
              Embed.
            </IgTypography>
          ),
          severity: "warning",
        };
      }

      if (scriptStatus.type === "multipleScriptsFound") {
        return {
          title: "Multiple Intelligems Scripts Detected",
          message: (
            <IgTypography>
              Multiple Intelligems scripts are detected. Please remove extra
              scripts.
            </IgTypography>
          ),
          severity: "warning",
        };
      }

      if (scriptStatus.type === "incorrectScriptFound") {
        return {
          title: "Incorrect Intelligems Script Detected",
          message: (
            <IgTypography>
              Expected script ending in{" "}
              <IgTypography variant={"code"}>
                {scriptStatus.expected}
              </IgTypography>{" "}
              but found{" "}
              <IgTypography variant={"code"}>{scriptStatus.found}</IgTypography>
              .
            </IgTypography>
          ),
          severity: "error",
        };
      }

      // if (scriptStatus.type === "inlineAsyncFound" && hasTestContent) {
      //   return {
      //     title: "Intelligems Script is Async or Deferred",
      //     message: (
      //       <IgTypography>
      //         Adding{" "}
      //         <IgTypography sx={{ fontFamily: "monospace", fontSize: "14px" }}>
      //           async
      //         </IgTypography>{" "}
      //         or{" "}
      //         <IgTypography sx={{ fontFamily: "monospace", fontSize: "14px" }}>
      //           defer
      //         </IgTypography>{" "}
      //         tags to the Intelligems script is not recommended when running
      //         content tests.{" "}
      //         <IgLink onClick={() => openNewTab(igDirectScriptAdd)}>
      //           Learn more.
      //         </IgLink>
      //       </IgTypography>
      //     ),
      //     severity: "warning",
      //   };
      // }
    }

    return null;
  }, [
    scriptStatus,
    org,
    experiencesList.isSuccess,
    experiencesList.data,
    location.pathname,
  ]);

  const button = useMemo(() => {
    if (scriptStatus.type === "inlineFound") {
      return {
        show: false,
        text: "",
        onClick: () => {},
      };
    }

    if (scriptStatus.type === "multipleScriptsWithAppEmbedFound") {
      return {
        show: true,
        text: "Remove App Embed",
        onClick: () => {
          openNewTab(
            `${
              org.shopId
            }/admin/themes/current/editor?context=apps&activateAppId=${
              import.meta.env.VITE_SHOPIFY_THEME_BLOCKS_UUID
            }/intelligems-script`,
            undefined,
            true
          );
        },
      };
    }

    if (scriptStatus.type === "multipleScriptsFound") {
      return {
        show: true,
        text: "Edit Theme",
        onClick: () => {
          openNewTab(
            `${org.shopId}/admin/themes/current/?key=layout%2Ftheme.liquid`,
            undefined,
            true
          );
        },
      };
    }

    return {
      show: true,
      text:
        scriptStatus.type === "inlineAsyncFound" ||
        scriptStatus.type === "appEmbedFound"
          ? "Modify Script"
          : "Add Script",
      onClick: () => {
        dispatch(setDialog({ dialog: "scriptTag", open: true }));
      },
    };
  }, [scriptStatus, org]);

  if (!content) {
    return null;
  }

  return (
    <IgAlert
      hideDismiss={hideDismiss}
      dismissId={dismissId ?? "script-status-alert"}
      title={content.title}
      message={content.message}
      severity={content.severity as AlertColor}
      buttons={[
        <IgButton
          key={"button-2"}
          loading={scriptStatusQuery.isFetching}
          variant={"text"}
          onClick={() =>
            void queryClient.invalidateQueries({
              queryKey: organizationScriptStatusKey.org(org.id),
            })
          }
        >
          Recheck
        </IgButton>,
        button.show ? (
          <IgButton
            key={"button-1"}
            variant={"contained"}
            onClick={button.onClick}
          >
            {button.text}
          </IgButton>
        ) : null,
      ]}
      sx={{
        marginBottom: "20px",
      }}
    />
  );
};

const HeadlessVersionStatusAlert = ({
  scriptStatus,
  org,
}: ChildScriptStatusAlertProps) => {
  const queryClient = useQueryClient();
  const scriptStatusQuery = useGetScriptStatus(org.id);
  const navigate = useNavigate();

  const content = useMemo(() => {
    if (scriptStatus.type === "headlessMissing") {
      return {
        title: "Intelligems Package Not Detected in your Theme",
        severity: "error",
        message: (
          <IgTypography>
            Please re-install the package and verify your{" "}
            <IgLink onClick={() => void navigate(SETTINGS_ROUTES.Settings)}>
              Domain Settings
            </IgLink>{" "}
            are up to date.
          </IgTypography>
        ),
      };
    }

    if (scriptStatus.type === "headlessDeprecatedFound") {
      return {
        title: "Intelligems Package Deprecated",
        severity: "error",
        message: (
          <IgTypography>
            Your Intelligems Package (v{scriptStatus.found}) is deprecated!
            Changes made throughout the app <b>will not</b> be reflected on your
            store until you update. The latest version is{" "}
            {scriptStatus.expected}.
          </IgTypography>
        ),
      };
    }

    if (scriptStatus.type === "headlessOutdatedFound") {
      return {
        title: "New Headless Package Version Available",
        severity: "warning",
        message: (
          <IgTypography>
            Your Intelligems Package (v{scriptStatus.found}) is out of date.
            Please update to {scriptStatus.expected} to ensure compatibility
            with our latest features.
          </IgTypography>
        ),
      };
    }

    return null;
  }, [org, scriptStatus]);

  if (!content) {
    return null;
  }
  return (
    <IgAlert
      dismissId={"script-status-alert"}
      title={content.title}
      message={content.message}
      longMessage={null}
      severity={content.severity as AlertColor}
      buttons={[
        <IgButton
          key={"button-1"}
          loading={scriptStatusQuery.isFetching}
          variant={"contained"}
          igVariant={content.severity as any}
          onClick={() =>
            void queryClient.invalidateQueries({
              queryKey: organizationScriptStatusKey.org(org.id),
            })
          }
        >
          Recheck
        </IgButton>,
      ]}
      sx={{ marginBottom: "20px" }}
    />
  );
};

interface ScriptStatusAlertProps {
  hideDismiss?: boolean;
  dismissId?: string;
}

export const ScriptStatusAlert = ({
  hideDismiss,
  dismissId,
}: ScriptStatusAlertProps) => {
  const org = useGetOrg();
  const organizationId = useGetOrgId();
  const scriptStatus = useGetScriptStatus(org.data?.org.id);

  if (scriptStatus.isSuccess && organizationId) {
    if (scriptStatus.data.type.includes("headless")) {
      return (
        <HeadlessVersionStatusAlert
          scriptStatus={scriptStatus.data}
          org={org.data.org}
        />
      );
    } else {
      return (
        <NormalScriptStatusAlert
          scriptStatus={scriptStatus.data}
          org={org.data.org}
          hideDismiss={hideDismiss}
          dismissId={dismissId}
        />
      );
    }
  }

  return null;
};
